import { Fragment, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Topbar, Footer, SEO } from '@components'
import { useStoreActions, useStoreState } from '@stores'
import {
	CLIENT_CODE,
	SITE_URL,
	SITE_NAME,
	SITE_DESCRIPTION
} from '@constants'

const formatPromoDate = (start?: string, end?: string): string => {
	if (!start && !end) return ''
	const fmt = (s?: string) => {
		if (!s) return ''
		const d = new Date(s)
		if (isNaN(d.getTime())) return s
		return d.toLocaleDateString('en-GB', {
			day: 'numeric',
			month: 'short',
			year: 'numeric'
		})
	}
	if (start && end) return `${fmt(start)} – ${fmt(end)}`
	return fmt(start || end)
}

export const Home = () => {
	const { fetchAllOutlet, fetchFeaturedOutlet } = useStoreActions(
		action => action.outlet
	)
	const { outlets, featuredOutlets } = useStoreState(state => state.outlet)

	const { fetchAllPromo } = useStoreActions(action => action.promo)
	const { promos } = useStoreState(state => state.promo)

	const { fetchAllEvent } = useStoreActions(action => action.event)
	const { events } = useStoreState(state => state.event)

	const { fetchAllArticle } = useStoreActions(action => action.article)
	const { articles } = useStoreState(state => state.article)

	const { fetchAllFacilities } = useStoreActions(action => action.facility)
	const { facilities } = useStoreState(state => state.facility)

	const { fetchAllCategory } = useStoreActions(action => action.category)
	const { categories } = useStoreState(state => state.category)

	useEffect(() => {
		fetchAllOutlet({
			page: 1,
			limit: 100,
			client_code: CLIENT_CODE
		})
	}, [fetchAllOutlet])

	useEffect(() => {
		fetchFeaturedOutlet({
			page: 1,
			limit: 8,
			client_code: CLIENT_CODE
		})
	}, [fetchFeaturedOutlet])

	useEffect(() => {
		fetchAllCategory({
			page: 1,
			limit: 100,
			client_code: CLIENT_CODE
		})
	}, [fetchAllCategory])

	useEffect(() => {
		fetchAllPromo({
			page: 1,
			limit: 100,
			client_code: CLIENT_CODE,
			status: 'all'
		})
	}, [fetchAllPromo])

	useEffect(() => {
		fetchAllEvent({
			page: 1,
			limit: 8,
			client_code: CLIENT_CODE,
			status: 'running'
		})
	}, [fetchAllEvent])

	useEffect(() => {
		fetchAllArticle({
			page: 1,
			limit: 100,
			client_code: CLIENT_CODE
		})
	}, [fetchAllArticle])

	useEffect(() => {
		fetchAllFacilities({
			page: 1,
			limit: 100,
			client_code: CLIENT_CODE
		})
	}, [fetchAllFacilities])

	const homeJsonLd = {
		'@context': 'https://schema.org',
		'@type': 'CollectionPage',
		name: SITE_NAME,
		url: `${SITE_URL}/`,
		description: SITE_DESCRIPTION,
		about: { '@id': `${SITE_URL}/#mall` },
		isPartOf: { '@id': `${SITE_URL}/#website` },
		hasPart: [
			{
				'@type': 'ItemList',
				name: 'Featured Stores',
				itemListElement: featuredOutlets.slice(0, 8).map((o, i) => ({
					'@type': 'ListItem',
					position: i + 1,
					url: `${SITE_URL}/directory/${o.slug}`,
					name: o.name
				}))
			},
			{
				'@type': 'ItemList',
				name: 'Promotions',
				itemListElement: (promos || []).slice(0, 8).map((p, i) => ({
					'@type': 'ListItem',
					position: i + 1,
					url: `${SITE_URL}/promotion/${p.slug}`,
					name: p.name
				}))
			}
		]
	}

	const breadcrumb = {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{
				'@type': 'ListItem',
				position: 1,
				name: 'Home',
				item: `${SITE_URL}/`
			}
		]
	}

	return (
		<Fragment>
			<SEO
				title="Botani Square Mall — Shopping, Dining & Lifestyle Center in Bogor"
				description={SITE_DESCRIPTION}
				path="/"
				keywords={[
					'Botani Square',
					'Mal Botani Square',
					'mall Bogor',
					'shopping Bogor',
					'event Bogor',
					'promo Bogor',
					'Pundis'
				]}
				jsonLd={[homeJsonLd, breadcrumb]}
			/>
			<Topbar />

			{/* Hero Section with Banner Image */}
			<section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24">
				{/* Background Banner Image */}
				<div className="absolute inset-0">
					<img
						src={require('@assets/images/home-banner.png')}
						alt="Botani Square Banner"
						className="w-full h-full object-cover"
					/>
					{/* Overlay for better text readability */}
					<div className="absolute inset-0 bg-black bg-opacity-40"></div>
				</div>

				{/* Content */}
				<div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
					<h1 className="text-5xl md:text-7xl font-bold mb-6">
						Welcome to <span className="text-primary">Botani Square</span>
					</h1>
					<h2 className="text-3xl md:text-5xl font-bold mb-6 text-gray-200">
						best shopping center in town
					</h2>
					<p className="hidden sm:block text-sm sm:text-base md:text-lg lg:text-xl text-gray-300 mb-4 sm:mb-6 md:mb-8 max-w-2xl mx-auto">
						Discover over 200 premium brands, world-class dining, and exclusive
						events in the heart of the city. Where luxury meets convenience.
					</p>
					<div className="flex flex-col sm:flex-row gap-4 justify-center">
						<a href="/directory">
							<button className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors">
								Explore Store →
							</button>
						</a>
						<a href="https://wa.me/6281277774798" target="_blank" rel="noreferrer">
							<button className="bg-white hover:bg-gray-100 text-gray-800 px-8 py-4 rounded-lg text-lg font-semibold transition-colors">
								Contact Us
							</button>
						</a>
					</div>
				</div>
			</section>

			{/* Promotions Section */}
			<section className="py-12 sm:py-16 md:py-20 bg-gray-800 text-white">
				<div className="max-w-screen-xl mx-auto px-4 sm:px-6">
					<div className="text-center mb-8 sm:mb-12 md:mb-16">
						<h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-2 sm:mb-3 md:mb-4">
							Promotions
						</h2>
						<p className="text-sm sm:text-base md:text-lg text-gray-300 max-w-2xl mx-auto">
							Don't miss out on our exclusive offers and deals.
						</p>
					</div>

					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
						{promos && promos.length > 0 ? (
							promos.slice(0, 3).map((promo, index) => {
								const dateLabel = formatPromoDate(
									promo.period?.start,
									promo.period?.end
								)
								return (
									<Link
										key={promo.id || index}
										to={`/promotion/${promo.slug}`}
										className="group flex flex-col bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
									>
										<div className="relative aspect-[2/1] bg-gradient-to-br from-blue-100 to-indigo-100 overflow-hidden">
											{promo.photos && promo.photos.length > 0 ? (
												<img
													src={promo.photos[0]}
													alt={promo.name || 'Promo Image'}
													className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
												/>
											) : (
												<div className="absolute inset-0 flex items-center justify-center text-4xl">
													🎉
												</div>
											)}
										</div>
										<div className="flex flex-col flex-1 p-5 sm:p-6">
											{dateLabel && (
												<div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
													{dateLabel}
												</div>
											)}
											<h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 line-clamp-2 flex-1">
												{promo.name || 'Promo Name'}
											</h3>
											<span className="inline-flex items-center gap-1 text-primary font-medium text-sm mt-auto group-hover:underline">
												View details
												<span aria-hidden>→</span>
											</span>
										</div>
									</Link>
								)
							})
						) : (
							<p className="col-span-full text-center text-gray-400">
								No promotions available.
							</p>
						)}
					</div>
				</div>
			</section>

			{/* Events Section */}
			<section className="py-12 sm:py-16 md:py-20 bg-white">
				<div className="max-w-screen-xl mx-auto px-4 sm:px-6">
					<div className="text-center mb-8 sm:mb-12 md:mb-16">
						<h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-2 sm:mb-3 md:mb-4">
							Events
						</h2>
						<p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
							Join us for exciting events, workshops, and entertainment
							throughout the year.
						</p>
					</div>

					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
						{events && events.length > 0 ? (
							events.slice(0, 3).map((event, index) => {
								const dateLabel = formatPromoDate(
									event.period?.start,
									event.period?.end
								)
								return (
									<Link
										key={event.id || index}
										to={`/event/${event.slug}`}
										className="group flex flex-col bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
									>
										<div className="relative aspect-square bg-gradient-to-br from-blue-100 to-indigo-100 overflow-hidden">
											{event.photos && event.photos.length > 0 ? (
												<img
													src={event.photos[0]}
													alt={event.name || 'Event Image'}
													className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
												/>
											) : (
												<div className="absolute inset-0 flex items-center justify-center text-4xl">
													🎉
												</div>
											)}
										</div>
										<div className="flex flex-col flex-1 p-5 sm:p-6">
											{dateLabel && (
												<div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
													{dateLabel}
												</div>
											)}
											<h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 line-clamp-2 flex-1">
												{event.name || 'Event Name'}
											</h3>
											<span className="inline-flex items-center gap-1 text-primary font-medium text-sm mt-auto group-hover:underline">
												View details
												<span aria-hidden>→</span>
											</span>
										</div>
									</Link>
								)
							})
						) : (
							<p className="col-span-full text-center text-gray-500">
								No events available.
							</p>
						)}
					</div>
				</div>
			</section>

			{/* Featured Stores Section */}
			<section className="py-20 bg-white">
				<div className="max-w-screen-xl mx-auto px-6">
					<div className="text-center mb-16">
						<h2 className="text-4xl font-bold text-gray-800 mb-4">
							Featured Stores
						</h2>
						<p className="text-lg text-gray-600 max-w-2xl mx-auto">
							Discover our carefully curated selection of premium brands and
							exclusive boutiques.
						</p>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
						{featuredOutlets.length > 0 ? (
							featuredOutlets.slice(0, 4).map((outlet, index) => (
								<Link
									key={outlet.id || index}
									to={`/directory/${outlet.slug}`}
									className="group bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
								>
									<div className="h-48 bg-white flex items-center justify-center overflow-hidden">
										{outlet.image ? (
											<img
												src={outlet.image}
												alt={outlet.name || 'Store Logo'}
												className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
											/>
										) : (
											<div className="text-4xl">🏪</div>
										)}
									</div>
									<div className="p-6">
										<div className="text-xs text-gray-500 uppercase tracking-wider mb-2">
											{outlet.store_category?.name || 'STORE'}
										</div>
										<h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-primary transition-colors">
											{outlet.name || 'Store Name'}
										</h3>
										<p className="text-gray-600 text-sm mb-4">
											{outlet.note || 'Premium store with exclusive products'}
										</p>
										<div className="flex items-center text-sm text-gray-500">
											<svg
												className="w-4 h-4 mr-2"
												fill="currentColor"
												viewBox="0 0 20 20"
											>
												<path
													fillRule="evenodd"
													d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
													clipRule="evenodd"
												/>
											</svg>
											{outlet.floor || 'Ground Floor'}
										</div>
									</div>
								</Link>
							))
						) : (
							<p>No featured stores available.</p>
						)}
					</div>
				</div>
			</section>

			{/* Shop by Category Section */}
			<section className="py-12 sm:py-16 md:py-20 bg-gray-50">
				<div className="max-w-screen-xl mx-auto px-4 sm:px-6">
					<div className="text-center mb-8 sm:mb-12 md:mb-16">
						<h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-2 sm:mb-3 md:mb-4">
							Shop by Category
						</h2>
					</div>

					<div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3 md:gap-4">
						{categories && categories.length > 0 ? (
							categories.map(category => (
								<Link
									key={category.id}
									to={`/directory?category=${encodeURIComponent(category.name)}`}
									className="bg-white border border-gray-200 rounded-lg px-2 sm:px-3 md:px-4 py-3 sm:py-4 md:py-6 text-xs sm:text-sm md:text-base text-gray-700 font-medium hover:border-primary hover:text-primary transition-colors text-center flex items-center justify-center min-h-[64px] sm:min-h-[72px] md:min-h-[96px]"
								>
									{category.name}
								</Link>
							))
						) : (
							<div className="col-span-full text-center text-gray-500 text-sm">
								Loading categories…
							</div>
						)}
					</div>
				</div>
			</section>

			{/* Promotions Section — hidden, hardcoded placeholders pending real content */}
			{false && (
				<section className="py-12 sm:py-16 md:py-20 bg-white">
					<div className="max-w-screen-xl mx-auto px-4 sm:px-6">
						<div className="text-center mb-8 sm:mb-12 md:mb-16">
							<h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-2 sm:mb-3 md:mb-4">
								Special Promotions
							</h2>
						</div>
					</div>
				</section>
			)}

			{/* Brand Directory Section */}
			<section className="py-12 sm:py-16 md:py-20 bg-gray-50">
				<div className="max-w-screen-xl mx-auto px-4 sm:px-6">
					<div className="text-center mb-8 sm:mb-12 md:mb-16">
						<h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-2 sm:mb-3 md:mb-4">
							Brand Directory
						</h2>
						<p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
							Explore Botani's Mall brand directory for exclusive deals and
							events tailored just for you!
						</p>
					</div>

					{outlets && outlets.length > 0 ? (
						<div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4 md:gap-6">
							{outlets.slice(0, 12).map((outlet, index) => (
								<Link
									key={outlet.id || index}
									to={`/directory/${outlet.slug}`}
									className="group bg-white rounded-lg p-3 sm:p-4 shadow-md hover:shadow-lg transition-shadow text-center"
								>
									<div className="h-16 sm:h-20 bg-white rounded-lg flex items-center justify-center mb-2 sm:mb-3 overflow-hidden">
										{outlet.image ? (
											<img
												src={outlet.image}
												alt={outlet.name || 'Brand Logo'}
												className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-300"
											/>
										) : (
											<div className="text-lg sm:text-2xl font-bold text-gray-600">
												{outlet.name
													? outlet.name.charAt(0).toUpperCase()
													: 'B'}
											</div>
										)}
									</div>
									<div className="text-xs sm:text-sm font-medium text-gray-700 truncate group-hover:text-primary transition-colors">
										{outlet.name || 'Brand Name'}
									</div>
								</Link>
							))}
						</div>
					) : (
						<div className="text-center py-12">
							<div className="text-4xl mb-4">🏪</div>
							<h3 className="text-xl font-bold text-gray-800 mb-2">
								No Brands Available
							</h3>
							<p className="text-gray-600">
								Check back later for brand listings!
							</p>
						</div>
					)}
				</div>
			</section>

			{/* Facilities Section */}
			<section className="py-12 sm:py-16 md:py-20 bg-white">
				<div className="max-w-screen-xl mx-auto px-4 sm:px-6">
					<div className="text-center mb-8 sm:mb-12 md:mb-16">
						<h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-2 sm:mb-3 md:mb-4">
							Botani Square has world class facilities
						</h2>
					</div>

					<div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
						{facilities.map(facility => (
							<div key={facility.name} className="text-center">
								<img
									src={facility.image}
									className="h-auto w-auto inline-block"
									alt="facilities"
								/>
								<div className="text-xs sm:text-sm font-medium text-gray-600 text-center">
									{facility.name}
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* News & Articles Section */}
			<section className="py-12 sm:py-16 md:py-20 bg-gray-50">
				<div className="max-w-screen-xl mx-auto px-4 sm:px-6">
					<div className="text-center mb-8 sm:mb-12 md:mb-16">
						<h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-2 sm:mb-3 md:mb-4">
							News & Articles
						</h2>
						<p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
							Discover our carefully curated selection of premium brands and
							exclusive boutiques.
						</p>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
						{articles && articles.length > 0 ? (
							articles.slice(0, 3).map((article, index) => (
								<div
									key={article.id || index}
									className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
								>
									<div className="h-40 sm:h-48 bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center overflow-hidden">
										{article.photos && article.photos.length > 0 ? (
											<img
												src={article.photos[0]}
												alt={article.name || 'Article Image'}
												className="w-full h-full object-cover"
											/>
										) : (
											<div className="text-2xl sm:text-3xl md:text-4xl">📰</div>
										)}
									</div>
									<div className="p-4 sm:p-6">
										<div className="text-xs text-gray-500 uppercase tracking-wider mb-2">
											NEWS
										</div>
										<h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">
											{article.name || 'Article Title'}
										</h3>
										<div
											className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4"
											dangerouslySetInnerHTML={{
												__html:
													article.description || 'Article description goes here'
											}}
										/>
										<a
											href={article.redirect_url || '#'}
											className="text-primary font-medium text-xs sm:text-sm hover:underline"
										>
											Read More...
										</a>
									</div>
								</div>
							))
						) : (
							<div className="col-span-3 text-center py-12">
								<p className="text-gray-500 text-lg">
									No articles available at the moment.
								</p>
							</div>
						)}
					</div>
				</div>
			</section>

			{/* Newsletter Section */}
			{/* <section className="py-12 sm:py-16 md:py-20 bg-gray-800 text-white">
				<div className="max-w-screen-xl mx-auto px-4 sm:px-6 text-center">
					<h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-2 sm:mb-3 md:mb-4">
						Stay Connected
					</h2>
					<p className="text-sm sm:text-base md:text-lg text-gray-300 mb-6 sm:mb-8 max-w-2xl mx-auto">
						Get the latest updates on new stores, events, and exclusive offers.
					</p>

					<div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-md mx-auto">
						<input
							type="email"
							placeholder="Enter your email address"
							className="flex-1 px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-primary focus:outline-none text-sm sm:text-base"
						/>
						<button className="bg-primary hover:bg-primary-dark text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-colors text-sm sm:text-base">
							Subscribe
						</button>
					</div>
				</div>
			</section> */}

			{/* Footer */}
			<Footer />
		</Fragment>
	)
}
